import api from './api';

export interface ServerListItem {
  id: string;
  name: string;
  host: string;
  port: number;
  user: string;
  default_db: string | null;
}

export interface ProcessRow {
  id: number;
  user: string | null;
  host: string | null;
  db: string | null;
  command: string | null;
  time: number | null;
  state: string | null;
  info: string | null;
}

export interface ServerInfo {
  version: string | null;
  uptime_seconds: number | null;
  status: Record<string, string>;
  variables: Record<string, string>;
}

export interface TableInfo {
  name: string;
  engine: string | null;
  rows: number | null;
  data_bytes: number | null;
  index_bytes: number | null;
}

export interface SchemaInfo {
  name: string;
  charset: string | null;
  tables: TableInfo[];
}

export interface UserInfo {
  user: string;
  host: string;
  grants: string[];
}

export interface ReplicationStatus {
  is_replica: boolean;
  raw: Record<string, string | null>;
}

export interface InnoDbStatus {
  raw: string;
}

const BASE = '/database-monitor';

const databaseMonitorService = {
  async listServers(signal?: AbortSignal): Promise<ServerListItem[]> {
    const res = await api.get<ServerListItem[]>(`${BASE}/servers`, { signal });
    return res.data;
  },

  async getProcesslist(serverId: string, signal?: AbortSignal): Promise<ProcessRow[]> {
    const res = await api.get<ProcessRow[]>(`${BASE}/servers/${serverId}/processlist`, { signal });
    return res.data;
  },

  async killProcess(serverId: string, pid: number): Promise<void> {
    await api.post(`${BASE}/servers/${serverId}/processlist/${pid}/kill`);
  },

  async getServerInfo(serverId: string, signal?: AbortSignal): Promise<ServerInfo> {
    const res = await api.get<ServerInfo>(`${BASE}/servers/${serverId}/details/server-info`, { signal });
    return res.data;
  },

  async getSchemas(serverId: string, signal?: AbortSignal): Promise<SchemaInfo[]> {
    const res = await api.get<SchemaInfo[]>(`${BASE}/servers/${serverId}/details/schemas`, { signal });
    return res.data;
  },

  async getUsers(serverId: string, signal?: AbortSignal): Promise<UserInfo[]> {
    const res = await api.get<UserInfo[]>(`${BASE}/servers/${serverId}/details/users`, { signal });
    return res.data;
  },

  async getReplication(serverId: string, signal?: AbortSignal): Promise<ReplicationStatus> {
    const res = await api.get<ReplicationStatus>(`${BASE}/servers/${serverId}/details/replication`, { signal });
    return res.data;
  },

  async getInnoDb(serverId: string, signal?: AbortSignal): Promise<InnoDbStatus> {
    const res = await api.get<InnoDbStatus>(`${BASE}/servers/${serverId}/details/innodb`, { signal });
    return res.data;
  },
};

export default databaseMonitorService;
